import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  DateAdapter,
  provideCalendar,
  CalendarPreviousViewDirective,
  CalendarTodayDirective,
  CalendarNextViewDirective,
  CalendarMonthViewComponent,
  CalendarWeekViewComponent,
  CalendarDayViewComponent,
  CalendarEvent,
  CalendarView,
  CalendarDatePipe,
} from 'angular-calendar';

import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { AppointmentStatus } from '../../core/enums/appointment-status.enum';
import { Appointment } from '../../core/models/appointment.model';
import { BookingSlot } from '../../core/models/booking-slot.model';
import { BookingService } from '../../core/services/booking.service';
import { NotificationService } from '../../core/services/notification.service';
import { Router } from '@angular/router';
import { SettingsService } from '../../core/services/settings.service';
import { AppearanceSettings } from '../../core/models/appearance-settings.model';
import { TodoService } from '../../core/services/todo.service';
import { TodoModel } from '../../core/models/todo.model';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'schedule-calendar-view',
  standalone: true,
  imports: [
    CommonModule,
    CalendarPreviousViewDirective,
    CalendarTodayDirective,
    CalendarNextViewDirective,
    CalendarMonthViewComponent,
    CalendarWeekViewComponent,
    CalendarDatePipe,
    CalendarDayViewComponent,
  ],
  templateUrl: './schedule-calendar-view.html',
  providers: [
    provideCalendar({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  styleUrls: ['./schedule-calendar-view.scss'],
})
export class ScheduleCalendarView implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() doctorId: string | null = null;
  @Input() appearanceSettings: AppearanceSettings | undefined;

  readonly CalendarView = CalendarView;

  viewDate = new Date();
  view: CalendarView = CalendarView.Month;

  upcomingBookings: Appointment[] = [];
  passedBookings: Appointment[] = [];
  ongoingBookings: Appointment[] = [];
  pendingBookings: Appointment[] = [];
  dailySchedule: BookingSlot[] = [];
  upcomingTodos: TodoModel[] = [];
  todayTodos: TodoModel[] = [];

  todosLoaded = false;

  events: CalendarEvent[] = [];

  constructor(
    private bookingsService: BookingService,
    private settingsService: SettingsService,
    private todoService: TodoService,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.loadAll();
    if (this.appearanceSettings?.scheduleCalendarView) {
      this.applyCalendarView(this.appearanceSettings.scheduleCalendarView);
    }
  }

  private applyCalendarView(view: string): void {
    this.view =
      view === 'WEEK' ? CalendarView.Week : view === 'DAY' ? CalendarView.Day : CalendarView.Month;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['doctorId'] && !changes['doctorId'].firstChange) {
      this.loadAll();
    }
    if (changes['appearanceSettings'] && this.appearanceSettings?.scheduleCalendarView) {
      this.applyCalendarView(this.appearanceSettings.scheduleCalendarView);
    }
  }

  // ----------------------------
  // LOAD ALL DATA
  // ----------------------------
  public loadAll(): void {
    this.todosLoaded = false;
    const today = new Date();
    const todayDateStr = today.toISOString().split('T')[0];

    forkJoin({
      upcomingBookings: this.bookingsService.getPatientBookingsByStatus(AppointmentStatus.Confirmed).pipe(
        catchError((err) => {
          this.notificationService.error(err?.error?.message || 'Failed to load upcoming bookings');
          return of([]);
        }),
      ),
      passedBookings: this.bookingsService.getPatientBookingsByStatus(AppointmentStatus.Completed).pipe(
        catchError((err) => {
          this.notificationService.error(err?.error?.message || 'Failed to load completed bookings');
          return of([]);
        }),
      ),
      pendingBookings: this.bookingsService.getPatientBookingsByStatus(AppointmentStatus.Pending).pipe(
        catchError((err) => {
          this.notificationService.error(err?.error?.message || 'Failed to load pending bookings');
          return of([]);
        }),
      ),
      ongoingBookings: this.bookingsService.getPatientBookingsByStatus(AppointmentStatus.Ongoing).pipe(
        catchError((err) => {
          this.notificationService.error(err?.error?.message || 'Failed to load ongoing bookings');
          return of([]);
        }),
      ),
      dailySchedule: this.bookingsService.getDailySchedule(this.doctorId).pipe(
        catchError((err) => {
          this.notificationService.error(err?.error?.message || 'Failed to load daily schedule');
          return of([]);
        }),
      ),
      upcomingTodos: this.todoService.getUpcomingTodos().pipe(
        catchError(() => of([])),
      ),
      todayTodos: this.todoService.getStaffTodos(todayDateStr).pipe(
        catchError(() => of([])),
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.upcomingBookings = res.upcomingBookings;
          this.passedBookings = res.passedBookings;
          this.pendingBookings = res.pendingBookings;
          this.ongoingBookings = res.ongoingBookings;
          this.dailySchedule = res.dailySchedule;
          this.upcomingTodos = res.upcomingTodos;
          this.todayTodos = res.todayTodos;
          this.todosLoaded = true;
          this.tryBuildCalendar();
        },
        error: () => {
          this.todosLoaded = true;
          this.tryBuildCalendar();
        },
      });
  }

  getUpcomingTodos(): void {
    this.todoService.getUpcomingTodos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.upcomingTodos = res;
          this.tryBuildCalendar();
        },
        error: () => {
          this.upcomingTodos = [];
          this.tryBuildCalendar();
        },
      });
  }

  getDailyTodos(): void {
    const today = new Date();
    const todayDateStr = today.toISOString().split('T')[0];

    this.todoService.getStaffTodos(todayDateStr)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.todayTodos = res;
          this.tryBuildCalendar();
        },
        error: () => {
          this.todayTodos = [];
          this.tryBuildCalendar();
        },
      });
  }

  // ----------------------------
  // CALENDAR BUILD
  // ----------------------------

  private tryBuildCalendar(): void {
    if (
      !this.upcomingBookings ||
      !this.passedBookings ||
      !this.ongoingBookings ||
      !this.dailySchedule ||
      !this.todosLoaded
    )
      return;

    this.mapToCalendarEvents();
  }

  private mapToCalendarEvents(): void {
    const events: CalendarEvent[] = [];

    // ----------------------------
    // CONFIRMED (UPCOMING)
    // ----------------------------
    this.upcomingBookings.forEach((a) => {
      events.push(
        this.createEvent(
          a,
          {
            primary: '#ef4444',
            secondary: '#fee2e2',
          },
          'Booked',
          a,
        ),
      );
    });

    // ----------------------------
    // ONGOING
    // ----------------------------
    this.ongoingBookings.forEach((a) => {
      events.push(
        this.createEvent(
          a,
          {
            primary: '#3b82f6',
            secondary: '#dbeafe',
          },
          'Ongoing',
          a,
        ),
      );
    });

    // ----------------------------
    // COMPLETED (PAST)
    // ----------------------------
    this.passedBookings.forEach((a) => {
      events.push(
        this.createEvent(
          a,
          {
            primary: '#6b7280',
            secondary: '#e5e7eb',
          },
          'Completed',
          a,
        ),
      );
    });

    // ----------------------------
    // PENDING
    // ----------------------------
    this.pendingBookings.forEach((a) => {
      events.push(
        this.createEvent(
          a,
          {
            primary: '#f59e0b',
            secondary: '#fef3c7',
          },
          'Pending',
          a,
        ),
      );
    });

    // ----------------------------
    // AVAILABLE SLOTS
    // ----------------------------
    this.dailySchedule.forEach((slot) => {
      const hasBooking = this.getSlotConsultation(slot);

      if (!hasBooking) {
        const start = new Date(slot.slotDate + 'T' + this.normalizeTime(slot.slotTime));

        events.push({
          start,
          title: 'Available Slot',
          color: {
            primary: '#22c55e',
            secondary: '#dcfce7',
          },
          meta: {
            booking: null,
          },
        });
      }
    });

    // ----------------------------
    // UPCOMING TODO TASKS
    // ----------------------------
    this.upcomingTodos.forEach((todo) => {
      const dateStr =
        typeof todo.todoDate === 'string' ? todo.todoDate.split('T')[0] : (todo.todoDate as any);
      const start = new Date(dateStr + 'T08:00:00');

      const priorityEmoji =
        todo.priority === 'HIGH' ? '🔴' : todo.priority === 'MEDIUM' ? '🟡' : '🟢';

      events.push({
        start,
        title: `${priorityEmoji} TODO: ${todo.title}`,
        color: {
          primary: '#15803d',
          secondary: '#dcfce7',
        },
        meta: {
          isTodo: true,
          todo,
        },
      });
    });

    // ----------------------------
    // TODAY'S TODO TASKS
    // ----------------------------
    this.todayTodos.forEach((todo) => {
      const dateStr =
        typeof todo.todoDate === 'string' ? todo.todoDate.split('T')[0] : (todo.todoDate as any);
      const start = new Date(dateStr + 'T08:00:00');

      const priorityEmoji =
        todo.priority === 'HIGH' ? '🔴' : todo.priority === 'MEDIUM' ? '🟡' : '🟢';

      events.push({
        start,
        title: `⭐ ${priorityEmoji} TODAY: ${todo.title}`,
        color: {
          primary: '#15803d',
          secondary: '#dcfce7',
        },
        meta: {
          isTodo: true,
          todo,
          isToday: true,
        },
      });
    });

    this.events = events;
  }

  private createEvent(
    a: Appointment,
    color: { primary: string; secondary: string },
    prefix: string,
    booking: Appointment,
  ): CalendarEvent {
    const start = new Date(
      a.bookingSlot.slotDate.split('T')[0] + 'T' + this.normalizeTime(a.bookingSlot.slotTime),
    );

    return {
      start,
      title: `${prefix}: ${a.patient.firstName} ${a.patient.lastName}`,
      color,
      meta: {
        booking,
      },
    };
  }

  // ----------------------------
  // HELPERS
  // ----------------------------

  getSlotConsultation(slot: BookingSlot): Appointment | null {
    const slotDateOnly = slot.slotDate.split('T')[0];

    const appointments = this.upcomingBookings.filter(
      (item) =>
        item.bookingSlot.slotDate.split('T')[0] === slotDateOnly &&
        this.normalizeTime(item.bookingSlot.slotTime) === this.normalizeTime(slot.slotTime),
    );

    return appointments.length ? appointments[0] : null;
  }

  private normalizeTime(time: string): string {
    return time ? time.replace('h', ':') : '';
  }

  // ----------------------------
  // NAVIGATION
  // ----------------------------

  handleEventClick(event: any): void {
    const meta = event.event.meta;

    // Todo events — do nothing (no navigation)
    if (meta?.isTodo) return;

    const booking = meta?.booking;
    if (!booking) return;

    this.router.navigate(['dashboard/view-booking'], {
      state: { booking },
    });
  }

  // ----------------------------
  // VIEW & DAY SELECTION
  // ----------------------------

  dayClicked(day: { date: Date }): void {
    if (day?.date) {
      this.viewDate = day.date;
      this.view = CalendarView.Day;
    }
  }

  dayHeaderClicked(day: { date: Date }): void {
    if (day?.date) {
      this.viewDate = day.date;
      this.view = CalendarView.Day;
    }
  }

  hourSegmentClicked(date: Date): void {
    if (date) {
      this.viewDate = date;
      this.view = CalendarView.Day;
    }
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  closeOpenMonthViewDay() {}
}
